import React from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { GridColumns } from "@mui/x-data-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { Typography } from "@mui/material";
import Table from "../../../reusable-components/Table/Table";
import { EmployeeCostSectionTypeApiService } from "../../../services/employee-cost-section-type/employee-cost-section-type-api.service";
import { RevenueExpenseTypesApiService } from "../../../services/revenue-expense-type/revenue-expense-type-api.service";
import { FormField } from "../../../reusable-components/Table/AddEditItem/interfaces";
import { FT_ECST } from "../../../utils/constants/filter-tables-constants";

const EmployeeCostSectionTypeTable = () => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.employee_cost_section_types";
  const theme = useTheme();
  const columns: GridColumns = [
    {
      field: "name",
      headerName: `${t(`common_words.name`)}`,
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "code",
      headerName: `${t(`common_words.code`)}`,
      headerAlign: "center",
      flex: 0.5,
    },
    {
      field: "description",
      headerName: `${t(`common_words.description`)}`,
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "isPricingRelevant",
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.is_pricing_relevant`)}`}</Typography>,
      headerAlign: "center",
      flex: 1,
      renderCell: ({ row }) =>
        row.isPricingRelevant ? (
          <FontAwesomeIcon
            icon={faCircleCheck}
            style={{ color: theme.palette.success.main }}
          />
        ) : (
          <></>
        ),
      align: "center",
    },
    {
      field: "revenueExpenseType",
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.revenue_expense_type`)}`}</Typography>,
      headerAlign: "center",
      flex: 1,
      renderCell: ({ row }) => <Typography>{row.revenueExpenseType?.name}</Typography>,
    },
    {
      field: "forecast",
      headerName: `${t(`${tRP}.fields.forecast`)}`,
      headerAlign: "center",
      flex: 1,
      renderCell: ({ row }) =>
        row.forecast ? (
          <FontAwesomeIcon
            icon={faCircleCheck}
            style={{ color: theme.palette.success.main }}
          />
        ) : (
          <></>
        ),
      align: "center",
    },
    {
      field: "isSocialSecurityRelevant",
      headerAlign: "center",
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.is_social_security_relevant`)}`}</Typography>,
      flex: 0.7,
      align: "center",
      renderCell: ({ row }) =>
        row.isSocialSecurityRelevant ? (
          <FontAwesomeIcon
            icon={faCircleCheck}
            style={{ color: theme.palette.success.main }}
          />
        ) : (
          <></>
        ),
    },
    {
      field: "isPaySchemaRelated",
      headerAlign: "center",
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.is_pay_schema_related`)}`}</Typography>,
      flex: 0.7,
      align: "center",
      renderCell: ({ row }) =>
        row.isPaySchemaRelated ? (
          <FontAwesomeIcon
            icon={faCircleCheck}
            style={{ color: theme.palette.success.main }}
          />
        ) : (
          <></>
        ),
    },
    {
      field: "usage",
      headerAlign: "center",
      headerName: `${t(`common_words.usage`)}`,
      flex: 0.7,
      align: "center",
      valueGetter: ({ row }) => row.usage,
    },
  ];

  const formFields: FormField[] = [
    {
      field: "name",
      fieldType: "textfield",
      labelKey: `${t(`common_words.name`)}`,
      required: true,
    },
    {
      field: "code",
      fieldType: "textfield",
      labelKey: `${t(`common_words.code`)}`,
      required: true,
    },
    {
      field: "description",
      fieldType: "textarea",
      labelKey: `${t(`common_words.description`)}`,
      required: false,
    },
    {
      field: "isPricingRelevant",
      fieldType: "checkbox",
      labelKey: `${t(`${tRP}.fields.is_pricing_relevant`)}`,
    },
    {
      field: "revenueExpenseType",
      fieldType: "dropdown",
      apiService: new RevenueExpenseTypesApiService(),
      labelKey: `${t(`${tRP}.fields.revenue_expense_type`)}`,
      required: false,
    },
    {
      field: "forecast",
      fieldType: "checkbox",
      labelKey: `${t(`${tRP}.fields.forecast`)}`,
    },
    {
      field: "isSocialSecurityRelevant",
      fieldType: "checkbox",
      labelKey: `${t(`${tRP}.fields.is_social_security_relevant`)}`,
    },
    {
      field: "isPaySchemaRelated",
      fieldType: "checkbox",
      labelKey: `${t(`${tRP}.fields.is_pay_schema_related`)}`,
    },
  ];

  return (
    <>
      <Table
        translationRootPath={tRP}
        serviceApi={new EmployeeCostSectionTypeApiService()}
        filter={FT_ECST}
        columns={columns}
        formFields={formFields}
        checkIsRowDeletable={(row) => row.usage === 0}
      />
    </>
  );
};
export default EmployeeCostSectionTypeTable;
